import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import {
  FornecimentoDiretoDetalhe,
  FornecimentoDiretoDetalheItem,
  FornecimentoDiretoDetalheItemMotivoRejeicao,
  FornecimentoDiretoDetalheMotivoRejeicao,
  Operadora,
  VinculoUsuarioOperadora,
} from '@gestor-web/@core/api/ords/model';
import { OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { pseudonimizar } from '@gestor-web/@core/utils';
import { Store } from '@ngrx/store';
import { buildGridOptions } from '@gestor-web/app/shared/components';
import { GeralService, NotificationService, PermissaoService } from '@gestor-web/app/shared/services';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridOptions } from 'ag-grid-community';
import { Subject, combineLatest, takeUntil } from 'rxjs';
import { ItemGroupCellRenderer } from './components/item-group/item-group-cell.component';
import { ItemDetalheCellRenderer } from './components/item-detalhe/item-detalhe-cell.component';
import { FornecimentoDiretoService } from '@gestor-web/app/pages/private/outras-remuneracoes/fornecimento-direto/fornecimento-direto.service';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

const COLUMNS_MOTIVOS_REJEICAO: ColDef[] = [
  {
    headerName: 'Campo',
    field: 'campo',
  },
  {
    headerName: 'Motivo rejeição',
    field: 'motivo_rejeicao',
    wrapText: true,
    autoHeight: true,
    width: 500,
  },
];

const COLUMNS_ITENS: ColDef[] = [
  {
    headerName: 'Nr. item guia',
    field: 'nr_item_guia',
    cellRenderer: 'agGroupCellRenderer', // necessário para expandir os detalhes
    cellRendererParams: {
      innerRenderer: ItemGroupCellRenderer,
    },
  },
  {
    headerName: '106-Tabela TUSS',
    field: 'cd_tabela_tuss',
  },
  {
    headerName: '107-Cód. grupo TUSS',
    field: 'cd_grupo_tuss',
  },
  {
    headerName: '108-Cód. item assistencial',
    field: 'cd_proced_item',
  },
  {
    headerName: '109-Qtd. informada',
    field: 'qt_item_fornecido',
    width: 100,
  },
  {
    headerName: '110-Valor item',
    field: 'vl_item_fornecido',
    type: 'money',
  },
  {
    headerName: '111-Valor coparticipação',
    field: 'vl_copartic_item',
    type: 'money',
  },
  {
    headerName: '127-Unidade de medida',
    field: 'cd_unidade_medida',
  },
];

@Component({
    selector: 'bl-fornecimento-direto-detalhe',
    templateUrl: './detalhe-fornecimento.component.html',
    styleUrls: ['./detalhe-fornecimento.component.scss'],
    standalone: false
})
export class FornecimentoDiretoDetalheLancamentoComponent implements OnInit, OnChanges {
  @ViewChild('agGridItens') agGridItens: AgGridAngular<FornecimentoDiretoDetalheItem>;
  @ViewChild('agGridMotivosRejeicao') agGridMotivosRejeicao: AgGridAngular<FornecimentoDiretoDetalheMotivoRejeicao>;

  gridOptionsItens: GridOptions<FornecimentoDiretoDetalheItem>;
  gridOptionsMotivosRejeicao: GridOptions<FornecimentoDiretoDetalheMotivoRejeicao>;

  @Input()
  operadora: string;

  @Input()
  cdGuiaMonitor: string;

  @Output()
  onChange = new EventEmitter<string>();

  operadoraWorkspace: Operadora | VinculoUsuarioOperadora;

  pseudonimizarAction: boolean = true;
  pseudonimizarActionVisible: boolean = true;

  data: RowDetalhe;
  dataOriginal: RowDetalhe;

  constructor(
    private permissaoService: PermissaoService,
    private service: FornecimentoDiretoService,
    private geralService: GeralService,
    private notificationService: NotificationService,
    private decimalPipe: DecimalPipe,
    private store: Store,
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.permissaoService.checkAcessoFeature(FUNCIONALIDADE.DESLIGAR_PSEUDONIMIZACAO),
      this.store.select(OperadorasSelectors.selectWorkspaceOperadora),
    ])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([podeDesligarPseudonimizacao, operadora]) => {
        this.operadoraWorkspace = operadora;

        if (operadora.ib_dado_sensivel_pseudonimizado) {
          this.pseudonimizarActionVisible = false;
        } else {
          const vinculo = this.operadoraWorkspace as VinculoUsuarioOperadora;
          this.pseudonimizarActionVisible = podeDesligarPseudonimizacao || !vinculo.ib_dado_sensivel_pseudonimizado_usuario;
        }
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptionsItens = buildGridOptions<FornecimentoDiretoDetalheItem>(
      {
        columnDefs: COLUMNS_ITENS,
        pipes: {
          decimal: this.decimalPipe,
        },
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
        groupDefaultExpanded: 0,
        masterDetail: true,
        detailCellRenderer: ItemDetalheCellRenderer,
      },
    );
    this.gridOptionsMotivosRejeicao = buildGridOptions<FornecimentoDiretoDetalheMotivoRejeicao>(
      {
        columnDefs: COLUMNS_MOTIVOS_REJEICAO,
      },
      {
        sideBar: null,
        statusBar: null,
        grandTotalRow: null,
      },
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    // this.innerHeight = `calc(${this.height ?? '90vh'} - ${HEIGHT_PADDING})`;

    if (!this.operadora || !this.cdGuiaMonitor) {
      return;
    }

    this.service.fetchDetalhes(this.operadora, this.cdGuiaMonitor).subscribe({
      next: response => {
        // console.log('response', response);
        const row = {
          ...response,
          nm_tp_registro: '',
          nm_id_situacao_lote: '',
          nm_id_sexo: '',

          row_itens_fornecidos: response.itens_fornecimento.map(subitem => {
            const motivos_rejeicao_item = subitem.motivos_rejeicao_item;

            const row_item: RowDetalheItem = {
              ...subitem,

              row_motivos_rejeicao_item: motivos_rejeicao_item.map(subitem => {
                const row_motivos_rejeicao: FornecimentoDiretoDetalheItemMotivoRejeicao = {
                  ...subitem,
                };
                return row_motivos_rejeicao;
              }),
            };
            return row_item;
          }),
        };

        row.nm_tp_registro = this.geralService.buildNomeDeDominio(row.tp_registro, 'dm_statusTransacaoMonitor');
        row.nm_id_situacao_lote = this.geralService.buildNomeDeDominio(row.id_situacao_lote, 'MON_LOTE_SITUACAO');
        row.nm_id_sexo = this.geralService.buildNomeDeDominio(row.id_sexo, 'dm_sexo');

        this.dataOriginal = row;
        this.pseudonimizarData();
      },
      error: error => this.notificationService.handleError(error),
    });
  }

  private pseudonimizarData() {
    if (!this.pseudonimizarActionVisible || !this.pseudonimizarAction) {
      this.data = this.dataOriginal;
      return;
    }

    this.data = {
      ...this.dataOriginal,

      nr_cns: pseudonimizar(this.dataOriginal.nr_cns),
      nr_cpf_beneficiario: pseudonimizar(this.dataOriginal.nr_cpf_beneficiario),
    };
  }

  togglePseudonimizar() {
    this.pseudonimizarAction = !this.pseudonimizarAction;
    this.pseudonimizarData();
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

export interface RowDetalhe extends FornecimentoDiretoDetalhe {
  nm_tp_registro: string;
  nm_id_situacao_lote: string;
  nm_id_sexo: string;

  row_itens_fornecidos: RowDetalheItem[];
}

export interface RowDetalheItem extends FornecimentoDiretoDetalheItem {
  row_motivos_rejeicao_item: FornecimentoDiretoDetalheItemMotivoRejeicao[];
}
